export const AGREEMENT_KEYS = ['termsChecked', 'privacyChecked', 'codeOfConductChecked'];

/**
 * Volunteers and educators must accept every agreement.
 * Strict `=== true` so a missing flag (undefined/null) counts as not accepted.
 * @param {Object} [userData] - Employee or educator data from the BE
 * @returns {boolean}
 */
export const hasAcceptedAgreements = (userData) => AGREEMENT_KEYS.every((key) => userData?.[key] === true);

/**
 * A volunteer or educator has completed onboarding when the BE marks them active
 * and they have accepted every agreement. This is the single source for the app's `active` flag.
 * @param {Object} [userData] - Employee or educator data from the BE
 * @returns {boolean}
 */
export const hasCompletedOnboarding = (userData) => !!userData?.isActive && hasAcceptedAgreements(userData);

/**
 * The BE can still hold `is_active = true` for someone who has not accepted every agreement
 * (e.g. an agreement was added or reset after they onboarded). Such a record must be flipped to inactive.
 * @param {Object} [userData] - Employee or educator data from the BE
 * @returns {boolean}
 */
export const needsDeactivation = (userData) => !!userData?.isActive && !hasAcceptedAgreements(userData);

/**
 * An org admin is also a volunteer. Org-admin mode only needs an active org (so an admin who skipped the
 * agreements can still reach the org setup). Volunteer mode additionally needs the admin's own employee record
 * to have completed onboarding.
 * @param {Object} options
 * @param {boolean} options.isVolunteerMode - The admin is using their volunteer account
 * @param {boolean} options.isOrgActive - The admin's organization is active
 * @param {Object} [options.employee] - The admin's own employee record from the BE
 * @returns {boolean}
 */
export const isOrgAdminActive = ({ isVolunteerMode, isOrgActive, employee }) => isOrgActive && (!isVolunteerMode || hasCompletedOnboarding(employee));

/**
 * Someone who is sent to profile setup but has been in the app before: the BE already holds them as active,
 * or they have accepted at least one agreement. A first-time user arrives with an empty record.
 * `isActive` alone is not enough, since a user who is deactivated for missing agreements loses it.
 * @param {Object} [userData] - Employee or educator data from the BE
 * @returns {boolean}
 */
export const isReturningUser = (userData) => !!userData?.isActive || AGREEMENT_KEYS.some((key) => userData?.[key] === true);

export const ONBOARDING_STATUS = Object.freeze({ NEW: 'new', RETURNING: 'returning' });

/**
 * @param {Object} [userData] - Employee or educator data from the BE
 * @returns {'new'|'returning'}
 */
export const resolveOnboardingStatus = (userData) => (isReturningUser(userData) ? ONBOARDING_STATUS.RETURNING : ONBOARDING_STATUS.NEW);
